import { fetchOrderItemFlightDetails } from 'actions/OrderActions'
import { ITEM_STATUS_CANCELLED } from 'constants/cart'
import { CHARTER } from 'constants/provider'
import { getAddedBaggageFromAddons } from './flightUtils'
import LineSeatIcon from 'LuxKit/Icons/line/LineSeatIcon'
import LineBagIcon from 'LuxKit/Icons/line/LineBagIcon'
import LineLuggageIcon from 'LuxKit/Icons/line/LineLuggageIcon'
import LineUtensilsIcon from 'LuxKit/Icons/line/LineUtensilsIcon'
import { allCountries } from 'country-telephone-data'
import type { AppDispatch } from 'src/client/store'
import { nonNullable } from 'lib/array/arrayUtils'
import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

// Fetch flight details for multiple flight items in order
export const fetchMultipleFlightDetails = (
  dispatch: AppDispatch,
  orderId: string,
  flightItems: Array<App.OrderFlightItem>,
) => {
  flightItems.forEach(item => {
    dispatch(fetchOrderItemFlightDetails(orderId, item.id))
  })
}

// Get all flight details from Redux state
export const getAllFlightDetails = (
  flightItems: Array<App.OrderFlightItem>,
  flightDetailsState: any,
): Array<App.FlightOrderItemDetails & { flightItemId: string }> => {
  return flightItems
    .map(item => {
      const flightDetails = flightDetailsState[item.id]?.flight
      if (!flightDetails) {
        return null
      }
      return {
        ...flightDetails,
        flightItemId: item.id,
      }
    })
    .filter(Boolean)
}

// Combine all legs from multiple flight items
export const combineAllFlightLegs = (
  allFlightDetails: Array<App.FlightOrderItemDetails & { flightItemId: string }>,
): Array<App.LegWithFlightItemId> => {
  return allFlightDetails.reduce((acc, flightDetails) => {
    if (flightDetails?.bookingJourney?.legs) {
      return [
        ...acc,
        ...flightDetails.bookingJourney.legs.map((leg) => {
          return {
            ...leg,
            flightItemId: flightDetails.flightItemId,
          }
        }),
      ]
    }
    return acc
  }, [])
}

// Check if any flight details from order are still loading
export const areFlightDetailsLoading = (
  flightItems: Array<App.OrderFlightItem>,
  flightDetailsState: any,
): boolean => flightItems.some(item => flightDetailsState[item.id]?.fetching)

// Get the added baggage for a traveller (if any)
export const travellerAddedBaggage = (
  traveller: App.FlightTraveller,
  selectedFlight: App.LegFlightWithAddons,
  intl: IntlShape,
) => {
  const addedBaggage = getAddedBaggageFromAddons(selectedFlight.addons.filter(addon => addon.travellerId === traveller.id))
  return formatAddedBaggage(addedBaggage, intl)
}

export function getTravellerFlightInclusions(
  selectedFlight: App.LegFlightWithAddons,
  traveller: App.FlightTraveller,
): App.LegFlightWithAddons['inclusions'] {
  const travellerIds = [
    traveller.id,
    traveller.provider_reference,
    traveller.mapped_traveler,
  ].filter(Boolean)

  const travellerFareDetails = selectedFlight.travellerFareDetails?.find(fareDetails => travellerIds.includes(fareDetails.travellerId))

  return travellerFareDetails?.inclusions ?? selectedFlight.inclusions
}

export function getTravellerFlightCabinLabel(
  selectedFlight: App.LegFlightWithAddons,
  traveller: App.FlightTraveller,
): string | undefined {
  if (!selectedFlight.hasMixedCabinClassLabels) {
    return
  }

  const travellerIds = [
    traveller.id,
    traveller.provider_reference,
    traveller.mapped_traveler,
  ].filter(Boolean)

  const travellerFareDetails = selectedFlight.travellerFareDetails?.find(fareDetails => travellerIds.includes(fareDetails.travellerId))

  return travellerFareDetails?.cabinClassLabel ?? selectedFlight.baseCabinLabel
}

const messages = defineMessages({
  purchasedBaggage: {
    id: 'XbAYcopr24Hb-7JBCeD5',
    defaultMessage: '{quantity}x {description} (purchased)',
    description: 'Included feature label for purchased checked baggage quantity and description',
  },
  notIncluded: {
    id: 'N3Djv-YtPplfZGFJnTeO',
    defaultMessage: 'Not included',
    description: 'Default label for an item that is not included in flight inclusions',
  },
})

const formatMessage = (
  intl: IntlShape,
  descriptor: MessageDescriptor,
  values?: Record<string, string | number>,
) => intl.formatMessage(descriptor, values)

export function formatAddedBaggage(addedBaggage: Record<string, number>, intl: IntlShape): Array<App.InclusionFeature> {
  return Object.entries(addedBaggage).map(([description, quantity]) => {
    return {
      description: formatMessage(intl, messages.purchasedBaggage, { quantity, description }),
      type: 'checked_baggage',
    }
  })
}

export function getDefaultNotIncludedText(key: string, intl: IntlShape) {
  switch (key) {
    case 'checkInBags':
    case 'cabinBags':
    case 'meal':
      return formatMessage(intl, messages.notIncluded)
    default:
      return undefined
  }
}

export const getFlightIngressIcon = (key: string) => {
  switch (key) {
    case 'seatReservation':
      return LineSeatIcon({ colour: 'light-foreground-alt' })
    case 'cabinBags':
      return LineBagIcon({ colour: 'light-foreground-alt' })
    case 'checkInBags':
      return LineLuggageIcon({ colour: 'light-foreground-alt' })
    case 'meal':
      return LineUtensilsIcon({ colour: 'light-foreground-alt' })
    default:
      return undefined
  }
}

/**
 * Extracts passport details from flights my escapes forms.
 */
export function hasIncompletePassport(traveller: App.FlightTraveller): boolean {
  return !traveller.passport?.number ||
    !traveller.passport.nationality ||
    !traveller.passport.country_of_issue ||
    !traveller.passport.expiry_date
}

export function hasAnyIncompletePassportDetails(
  allFlightDetails: Array<{ flightDetails: App.FlightOrderItemDetails | undefined }>,
): boolean {
  return allFlightDetails.some(({ flightDetails }) => {
    const isCharter = flightDetails?.bookingJourney?.legs?.some(leg => leg.provider === CHARTER) ?? false
    return flightDetails?.travellers.some(t => {
      const passportRequired = isCharter || t.included_passport_details
      return passportRequired && hasIncompletePassport(t)
    })
  })
}

export function extractPassportDetails(formData: Record<string, any>): App.FlightTravellerPassport | undefined {
  const { nationality, passportCountry, passportExpiry, passportNumber } = formData

  if (!nationality || !passportCountry || !passportExpiry || !passportNumber) {
    return
  }

  return {
    nationality,
    number: passportNumber,
    expiry_date: passportExpiry,
    country_of_issue: passportCountry,
  }
}

export function extractEmergencyContact(formData: Record<string, any>): App.FlightEmergencyContact | undefined {
  const {
    emergencyFirstName,
    emergencyLastName,
    emergencyRelationship,
  } = formData

  const emergencyPhone = formData['emergencyPhone.phone']
  const emergencyPhonePrefix = formData['emergencyPhone.prefix']

  if (
    !emergencyFirstName ||
    !emergencyLastName ||
    !emergencyPhone ||
    !emergencyPhonePrefix ||
    !emergencyRelationship
  ) {
    return undefined
  }

  const country = allCountries.find((c) => c.dialCode === emergencyPhonePrefix)

  return {
    firstName: emergencyFirstName,
    lastName: emergencyLastName,
    phone: `${emergencyPhonePrefix}${emergencyPhone}`,
    relationship: emergencyRelationship,
    countryCode: country?.iso2.toUpperCase() ?? '',
  }
}

/**
 * Gets the earliest departure and latest arrival dates from flight details.
 * Uses only the first flight segment of the first leg of each flight item to match
 * the old order.flightItems behavior (which only stored journey.departing.flights[0] dates).
 */
export const getFlightDatesFromDetails = (
  flightItems: Array<App.OrderFlightItem>,
  flightDetailsState: App.OrdersState['flightDetails'],
): { earliest: string | undefined, latest: string | undefined } => {
  const allFlightDetails = getAllFlightDetails(flightItems, flightDetailsState)

  if (allFlightDetails.length === 0) {
    return { earliest: undefined, latest: undefined }
  }

  const flightItemDates = allFlightDetails
    .map(details => {
      const firstLeg = details.bookingJourney?.legs?.[0]
      const firstSegment = firstLeg?.flights?.[0]
      return {
        departure: firstSegment?.departingDate,
        arrival: firstSegment?.arrivalDate,
      }
    })
    .filter(d => d.departure || d.arrival)

  const departureDates = flightItemDates.map(d => d.departure).filter(Boolean).sort()
  const arrivalDates = flightItemDates.map(d => d.arrival).filter(Boolean).sort()

  return {
    earliest: departureDates.length > 0 ? departureDates[0] : undefined,
    latest: arrivalDates.length > 0 ? arrivalDates[arrivalDates.length - 1] : undefined,
  }
}

/**
 * Gets travel durations from non-flight order items.
 */
const getNonFlightTravelDurations = (order: App.Order) => {
  const nonCancelledItems = order.items.filter(item => item.status !== ITEM_STATUS_CANCELLED)
  return nonNullable([
    ...(nonCancelledItems.length > 0 ? nonCancelledItems : order.items).map(item => ({
      startDate: item.reservation?.startDate,
      endDate: item.reservation?.endDate,
    })),
    ...order.bedbankItems.filter(item => item.status !== 'cancelled').map(item => ({
      startDate: item.checkIn.format('YYYY-MM-DD'),
      endDate: item.checkOut.format('YYYY-MM-DD'),
    })),
    ...order.cruiseItems.map(item => ({
      startDate: item.departureDate,
      endDate: item.arrivalDate,
    })),
    ...order.tourItems.map(item => ({
      startDate: item.tour.startDate,
      endDate: item.tour.endDate,
    })),
    ...order.experienceItems.map(item => ({
      startDate: item.date,
      endDate: item.date,
    })),
    ...order.carHireItems.map(item => ({
      startDate: item.reservation?.pickUp.date,
      endDate: item.reservation?.dropOff.date,
    })),
    ...order.customOfferItems.map(item => ({
      startDate: item.custom_offer.items[0]?.start_date,
      endDate: item.custom_offer.items[0]?.end_date,
    })),
  ].filter(item => item.startDate || item.endDate))
}

/**
 * Gets the order travel duration using flight details for accurate flight dates.
 * For bundled orders, combines flight dates from flightDetails with other item dates from order.
 * Returns undefined dates if flight items exist but flight details haven't been fetched yet.
 */
export const getOrderTravelDurationWithFlightDetails = (
  order: App.Order,
  flightDetailsState: App.OrdersState['flightDetails'],
): { earliest: string | undefined, latest: string | undefined } => {
  const hasFlightItems = order.flightItems.length > 0

  // Get flight dates from flight details (accurate after date changes)
  let flightDates: { earliest: string | undefined, latest: string | undefined } = { earliest: undefined, latest: undefined }
  if (hasFlightItems) {
    flightDates = getFlightDatesFromDetails(order.flightItems, flightDetailsState)

    if (!flightDates.earliest || !flightDates.latest) {
      return { earliest: undefined, latest: undefined }
    }
  }

  const nonFlightDates = getNonFlightTravelDurations(order)

  const allStartDates: Array<string> = []
  const allEndDates: Array<string> = []

  nonFlightDates.forEach(d => {
    if (d.startDate) allStartDates.push(d.startDate)
    if (d.endDate) allEndDates.push(d.endDate)
  })

  if (hasFlightItems && flightDates.earliest && flightDates.latest) {
    allStartDates.push(flightDates.earliest)
    allEndDates.push(flightDates.latest)
  }

  const sortedStartDates = allStartDates.sort()
  const sortedEndDates = allEndDates.sort()

  return {
    earliest: sortedStartDates.length > 0 ? sortedStartDates[0] : undefined,
    latest: sortedEndDates.length > 0 ? sortedEndDates[sortedEndDates.length - 1] : undefined,
  }
}

/**
 * Extracts country code from phone number.
 * Removes NANP matches to avoid getting area codes within the +1 region.
 */
export function extractPhoneNumber(phone: string) {
  const dialCodes = allCountries.map((c) => c.dialCode)
    .filter((c) => c.length <= 3) // remove NANP matches
    .sort((a, b) => Number(b) - Number(a)) //

  for (const countryCode of dialCodes) {
    if (phone.startsWith(countryCode)) {
      return {
        countryCode,
        number: phone.slice(countryCode.length),
      }
    }
  }

  return {
    countryCode: null,
    number: phone,
  }
}
